import React, { useState, useEffect } from "react";
import { ExternalLink, Copy, HardDrive, Video, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSiteContext } from "../../../../context/SiteContext";
import requests from "../../../../services/httpServices";

const businessFeatures = [
  "5 Rooms (running in parallel)",
  "100 interactive participants per meeting",
  "Webinars up to 1,000 attendees",
  "Unlimited breakouts + whiteboards",
  "Full Branding Suite",
  "R-Link AI Suite (Notetaker + Translation)",
  "50 GB storage",
  "Phone dial-in + conferencing",
];

const basicFeatures = [
  "1 Room",
  "Up to 50 interactive participants",
  "1 Breakout Room + 1 Whiteboard",
  "Core Media: Slides, Video, Audio",
  "10 GB storage",
  "Branded backgrounds",
  "Admin portal included",
];

const FALLBACK_ROOM_DATA = {
  hasRoom: false,
  roomName: null,
  plan: "Basic",
  status: "active",
  storageUsed: 0,
  storageLimit: 10,
  meetingsThisMonth: 0,
  joinUrl: null,
  nextBilling: "N/A",
  monthlyFee: 0,
};

export default function MemberRoom() {
  const { user } = useSiteContext();
  const navigate = useNavigate();
  const [roomData, setRoomData] = useState(null);
  const [dismissedAlert, setDismissedAlert] = useState(false);
  const [copiedFeedback, setCopiedFeedback] = useState(false);

  useEffect(() => {
    if (!user?.memberId) return;
    let cancelled = false;
    const fetchRoom = async () => {
      try {
        const data = await requests.get(`/member/${user.memberId}/room`);
        if (!cancelled && data?.success && data?.data) {
          setRoomData(data.data);
        } else if (!cancelled) {
          setRoomData({ ...FALLBACK_ROOM_DATA });
        }
      } catch {
        if (!cancelled) setRoomData({ ...FALLBACK_ROOM_DATA });
      }
    };
    fetchRoom();
    return () => { cancelled = true; };
  }, [user?.memberId]);

  if (!roomData) {
    return (
      <div style={{ padding: 40, color: "rgba(255,255,255,0.4)", fontSize: 14 }}>Loading...</div>
    );
  }

  const hasRoom = roomData.hasRoom === true;
  const storagePercentage = roomData.storageLimit > 0 ? (roomData.storageUsed / roomData.storageLimit) * 100 : 0;
  const showStorageAlert = storagePercentage > 80 && !dismissedAlert;
  const storageColor = storagePercentage < 60 ? "#00c853" : storagePercentage < 80 ? "#f59e0b" : "#ef4444";

  const handleCopyLink = () => {
    if (!roomData.joinUrl) return;
    navigator.clipboard.writeText(roomData.joinUrl);
    setCopiedFeedback(true);
    setTimeout(() => setCopiedFeedback(false), 2000);
  };

  const features = roomData.plan === "Business" ? businessFeatures : basicFeatures;
  const memberName = user?.name || user?.email || "Member";

  return (
    <div style={{ padding: "8px 16px 40px", fontFamily: "'DM Sans', sans-serif", color: "#ffffff" }}>
      {showStorageAlert && (
        <div
          style={{
            padding: "16px 20px",
            borderRadius: 12,
            background: "rgba(245,158,11,0.1)",
            border: "1px solid rgba(245,158,11,0.3)",
            color: "#f59e0b",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 32,
            fontSize: 14,
          }}
        >
          <span>Your room is at {Math.round(storagePercentage)}% of storage capacity. Consider deleting old recordings to free up space.</span>
          <button onClick={() => setDismissedAlert(true)} style={{ background: "none", border: "none", color: "#f59e0b", cursor: "pointer", fontSize: 18 }}>×</button>
        </div>
      )}

      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", marginBottom: 8 }}>
          MY ROOM
        </div>
        <h1 style={{ fontSize: 30, fontWeight: 800, color: "#ffffff", margin: "0 0 8px 0" }}>
          Welcome back, {memberName}
        </h1>
        <p style={{ fontSize: 15, color: "rgba(255,255,255,0.6)", margin: 0 }}>
          Here's everything you need to access and manage your meeting room.
        </p>
      </div>

      {!hasRoom ? (
        <div
          style={{
            background: "linear-gradient(135deg, #001233, #1e0049)",
            border: "1px solid rgba(106,31,191,0.3)",
            borderRadius: 20,
            padding: "48px 40px",
            marginBottom: 32,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 22, fontWeight: 700, color: "#ffffff", marginBottom: 8 }}>No Room Assigned Yet</div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", maxWidth: 400, margin: "0 auto" }}>
            Your account is set up but a meeting room hasn't been assigned to you yet. Please contact your administrator for room access.
          </div>
        </div>
      ) : (
        <div
          style={{
            background: "linear-gradient(135deg, #001233, #1e0049)",
            border: "1px solid rgba(106,31,191,0.3)",
            borderRadius: 20,
            padding: "36px 40px",
            marginBottom: 32,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 40,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.45)", marginBottom: 8 }}>
              YOUR MEETING ROOM
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#ffffff", fontFamily: "monospace", marginBottom: 12 }}>
              {roomData.roomName}
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: roomData.plan === "Business" ? "rgba(106,31,191,0.2)" : "rgba(0,200,83,0.2)", color: roomData.plan === "Business" ? "#a64dff" : "#00c853" }}>
                {roomData.plan} Plan
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 6, color: roomData.status === "active" ? "#00c853" : "#ef4444", fontSize: 14, fontWeight: 600 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: roomData.status === "active" ? "#00c853" : "#ef4444" }} />
                {roomData.status === "active" ? "Active" : "Storage Flagged"}
              </span>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <a
              href={roomData.joinUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                padding: "16px 36px", background: "linear-gradient(135deg, #00c853, #1a6b3c)",
                color: "#ffffff", fontSize: 16, fontWeight: 700, borderRadius: 12,
                textDecoration: "none",
              }}
            >
              Join Room <ExternalLink size={18} />
            </a>
            <button
              onClick={handleCopyLink}
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                padding: "10px 16px", background: "transparent",
                border: "1px solid rgba(255,255,255,0.2)", color: "#ffffff",
                fontSize: 14, fontWeight: 500, borderRadius: 8, cursor: "pointer",
              }}
            >
              <Copy size={14} /> {copiedFeedback ? "Copied!" : "Copy Room Link"}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20, marginBottom: 32 }}>
        <StatCard
          label="STORAGE USED"
          value={`${roomData.storageUsed} GB`}
          sub={`of ${roomData.storageLimit} GB included`}
          icon={<HardDrive size={24} color="#6a1fbf" />}
          footer={
            <div style={{ width: "100%", height: 6, borderRadius: 3, background: "rgba(255,255,255,0.1)", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${storagePercentage}%`, background: storageColor }} />
            </div>
          }
        />
        <StatCard
          label="MEETINGS THIS MONTH"
          value={roomData.meetingsThisMonth}
          valueColor="#00c853"
          sub="This month"
          icon={<Video size={24} color="#00c853" />}
        />
        <StatCard
          label="NEXT BILLING DATE"
          value={roomData.nextBilling || "N/A"}
          sub={`$${roomData.monthlyFee}/mo · ${roomData.plan} Plan`}
          icon={<Calendar size={24} color="#6a1fbf" />}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)", gap: 20 }}>
        <div style={{ padding: 24, borderRadius: 14, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: "#ffffff", marginTop: 0, marginBottom: 20 }}>Your Plan Features</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {features.map((feature, i) => (
              <div key={i} style={{ fontSize: 14, color: "rgba(255,255,255,0.8)", display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ color: "#00c853", fontWeight: 600 }}>✓</span>
                {feature}
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: 24, borderRadius: 14, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: "#ffffff", marginTop: 0, marginBottom: 16 }}>Quick Actions</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {[
              { label: "View Billing & Invoices", path: "/dashboard/billing" },
              { label: "Submit Support Ticket", path: "/dashboard/support" },
              { label: "Edit Profile", path: "/dashboard/profile" },
            ].map((action, i) => (
              <button
                key={i}
                onClick={() => navigate(action.path)}
                style={{
                  padding: "14px 16px", borderRadius: 10, border: "none",
                  background: "transparent", cursor: "pointer",
                  fontSize: 14, color: "#ffffff",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  textAlign: "left",
                }}
              >
                <span>{action.label}</span>
                <span>→</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, valueColor, sub, icon, footer }) {
  return (
    <div style={{ padding: 24, borderRadius: 14, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", marginBottom: 4 }}>
            {label}
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: valueColor || "#ffffff" }}>{value}</div>
          {sub && <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>{sub}</div>}
        </div>
        {icon}
      </div>
      {footer}
    </div>
  );
}
